import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Controller, useForm } from 'react-hook-form'
import { makeStyles } from '@material-ui/core'
import clsx from 'clsx'
import SydModal from '../../commonDesign/SydModal'
import SydAutocomplete from '../../commonDesign/SydAutocomplete'
import SydLabel from '../../commonDesign/SydLabel'
import SydButton from '../../commonDesign/Button'
import { useRedtailContactSearch } from '../../../api/redtail'
import RedtailFamilyPreview from './RedtailFamilyPreview'
import { familyNameDefault, contactDisplayName } from './helpers'

const useStyles = makeStyles({
  errorRow: { color: 'var(--color-decline-500)' }
})

const FORM_DEFAULTS = { selectedContact: null, familyName: '' }

function toContactOption (c) {
  if (!c) return null
  return { value: c.id, label: contactDisplayName(c), raw: c }
}

function RedtailClientLinkDialog ({ open, onClose, state, currentClientName }) {
  const classes = useStyles()
  const [searchInput, setSearchInput] = useState('')
  const [familyResolved, setFamilyResolved] = useState(null)

  const { control, handleSubmit, reset, watch, setValue } = useForm({
    defaultValues: FORM_DEFAULTS
  })
  const selectedContact = watch('selectedContact')
  const familyNameValue = watch('familyName')
  const step = selectedContact ? 'family' : 'search'

  const search = useRedtailContactSearch(searchInput, { clientId: state.clientId })
  const options = useMemo(
    () => (search.data ?? []).map(toContactOption).filter(Boolean),
    [search.data]
  )

  const handleClose = useCallback(() => {
    reset(FORM_DEFAULTS)
    setSearchInput('')
    setFamilyResolved(null)
    state.resetConflict()
    onClose()
  }, [onClose, reset, state])

  const handleBack = useCallback(() => {
    reset(FORM_DEFAULTS)
    setSearchInput('')
    setFamilyResolved(null)
    state.resetConflict()
    state.resetMutationError()
  }, [reset, state])

  const onSubmit = useCallback(async (values) => {
    if (!values.selectedContact) return
    try {
      await state.link({
        primaryContactId: values.selectedContact.id,
        familyName: familyResolved?.hasFamily ? undefined : values.familyName,
        acknowledgeConflict: !!state.conflict
      })
      handleClose()
    } catch {
      // 409s surface via state.conflict; other errors via showInlineError.
    }
  }, [state, familyResolved, handleClose])

  const ctaLabel = state.conflict
    ? `Yes, also link to ${currentClientName || 'this client'}`
    : 'Link Client'

  const submitDisabled = useMemo(() => {
    if (state.isLinking) return true
    if (!selectedContact || !familyResolved) return true
    if (!familyResolved.hasFamily && !(familyNameValue || '').trim()) return true
    return false
  }, [selectedContact, familyResolved, state.isLinking, familyNameValue])

  const trimmedInput = searchInput.trim()
  const belowMinChars = trimmedInput.length < 2
  // Empty recently-viewed list is normal; don't surface it as "no matches".
  const showNoMatches = !belowMinChars && !search.isFetching && options.length === 0
  const searchDescription = belowMinChars
    ? 'Showing your recently viewed Redtail contacts. Type to search by name.'
    : undefined

  const showInlineError = state.mutationError && state.mutationError?.response?.status !== 409

  return (
    <SydModal
      open={open}
      onClose={handleClose}
      title='Link Client to Redtail'
      size='medium'
    >
      <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col gap-lg'>
        {step === 'search' && (
          <SydLabel label='Find primary contact' description={searchDescription}>
            <Controller
              name='selectedContact'
              control={control}
              render={({ field }) => (
                <SydAutocomplete
                  placeholder='Search by name…'
                  options={options}
                  value={null}
                  onInputChange={(_e, val) => setSearchInput(val ?? '')}
                  onChange={(_e, option) => {
                    const raw = option?.raw ?? null
                    field.onChange(raw)
                    if (raw) {
                      setValue('familyName', familyNameDefault(raw))
                      setFamilyResolved(null)
                      state.resetConflict()
                      state.resetMutationError()
                    }
                  }}
                  isLoading={search.isFetching}
                />
              )}
            />
            {showNoMatches && (
              <div className='font-size-sm opacity-lowlighted mt-1'>
                No Redtail contacts match. Make sure the contact exists in Redtail and try again.
              </div>
            )}
          </SydLabel>
        )}

        {step === 'family' && (
          <RedtailFamilyPreview
            contact={selectedContact}
            control={control}
            conflict={state.conflict}
            currentClientName={currentClientName}
            onResolved={setFamilyResolved}
          />
        )}

        {showInlineError && (
          <div className={clsx(classes.errorRow, 'font-size-sm')}>
            {state.mutationError.message || 'Failed to link client. Please try again.'}
          </div>
        )}

        <div className='flex justify-between w-full gap-md'>
          <div className='flex gap-md'>
            {step === 'family' ? (
              <SydButton variant='secondary' onClick={handleBack} type='button' icon='back'>
                Back
              </SydButton>
            ) : (
              <SydButton variant='secondary' onClick={handleClose} type='button'>
                Cancel
              </SydButton>
            )}
          </div>
          {step === 'family' && (
            <SydButton
              variant='primary'
              type='submit'
              processing={state.isLinking}
              disabled={submitDisabled}
            >
              {ctaLabel}
            </SydButton>
          )}
        </div>
      </form>
    </SydModal>
  )
}

RedtailClientLinkDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  state: PropTypes.object.isRequired,
  currentClientName: PropTypes.string
}

export default RedtailClientLinkDialog
